/* eslint-disable react/prop-types */
import { Descriptions } from 'antd';
import { useFormikContext } from 'formik';
import { keyBy, uniq } from 'lodash/fp';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    type FinderApplicationEntrypointSpecsFragment,
    type RouterFinderApplicationPrivateModuleFragment,
} from '../../../../../api/fragments';
import {
    CreateFinderApplicationEntrypointDocument,
    type CreateFinderApplicationEntrypointMutation,
    type CreateFinderApplicationEntrypointMutationVariables,
} from '../../../../../api/mutations/createFinderApplicationEntrypoint';
import {
    UpdateFinderApplicationEntrypointDocument,
    type UpdateFinderApplicationEntrypointMutation,
    type UpdateFinderApplicationEntrypointMutationVariables,
} from '../../../../../api/mutations/updateFinderApplicationEntrypoint';
import { type FinderApplicationPublicAccessEntrypointSettings } from '../../../../../api/types';
import InputField from '../../../../../components/fields/InputField';
import SelectField, { type SelectFieldProps } from '../../../../../components/fields/SelectField';
import { StyledBulletsList } from './FinderApplicationPublicAccessEntrypoint';
import { type EndpointImplementation, type EndpointViewPanelProps } from './types';

const mapOption = (acc, module) => {
    if (module.__typename === 'FinderApplicationPrivateModule') {
        return [...acc, { value: module.id, label: module.displayName }];
    }

    return acc;
};

type FormValues = FinderApplicationPublicAccessEntrypointSettings & { finderApplicationModuleIds?: string[] };

const implementation: EndpointImplementation<FinderApplicationEntrypointSpecsFragment, FormValues> = {
    getTitle: t => t('routerDetails:endpoints.FinderApplicationEntrypoint.title'),
    ViewPanelComponent: ({ endpoint }: EndpointViewPanelProps<FinderApplicationEntrypointSpecsFragment>) => (
        <Descriptions size="small" bordered>
            <Descriptions.Item label="Modules">
                {endpoint.finderApplicationModules?.length > 1 ? (
                    <StyledBulletsList>
                        {endpoint.finderApplicationModules.map(module => (
                            <li key={module.id}>{module.displayName}</li>
                        ))}
                    </StyledBulletsList>
                ) : (
                    <div>{endpoint.finderApplicationModules[0].displayName} </div>
                )}
            </Descriptions.Item>
            <Descriptions.Item label="Display Name">{endpoint.displayName}</Descriptions.Item>
        </Descriptions>
    ),
    getIsPrivate: () => true,
    FormComponent: ({ router }) => {
        const { t } = useTranslation(['routerDetails']);
        const { values } = useFormikContext<FormValues>();

        const finderApplicationModules: RouterFinderApplicationPrivateModuleFragment[] = useMemo(
            () =>
                router.company.modules.filter(
                    module => module.__typename === 'FinderApplicationPrivateModule'
                ) as RouterFinderApplicationPrivateModuleFragment[],
            [router.company.modules]
        );
        const { finderApplicationModuleMapping, conditions } = useMemo(() => {
            const selectedFinderApplicationModules = finderApplicationModules.filter(({ id }) =>
                values.finderApplicationModuleIds?.includes(id)
            );

            return {
                finderApplicationModuleMapping: keyBy('id', selectedFinderApplicationModules),
                conditions: uniq(
                    selectedFinderApplicationModules.flatMap(({ finderVehicleConditions }) => finderVehicleConditions)
                ),
            };
        }, [finderApplicationModules, values.finderApplicationModuleIds]);

        const finderApplicationModule = useMemo(() => {
            if (!values.finderApplicationModuleIds?.length) {
                return null;
            }

            return finderApplicationModules.find(({ id, __typename }) => id === values.finderApplicationModuleIds[0]);
        }, [finderApplicationModules, values]);

        const options = useMemo(() => {
            if (!finderApplicationModule) {
                return router.company.modules.reduce<SelectFieldProps['options']>(mapOption, []);
            }

            return finderApplicationModules
                .filter(module => {
                    if (finderApplicationModule.__typename !== 'FinderApplicationPrivateModule') {
                        return false;
                    }

                    const { vehicleModuleId, id, finderVehicleConditions, bankModuleId } = module;

                    if (finderApplicationModuleMapping[id]) {
                        return true;
                    }

                    return (
                        // should be the same vehicle module id
                        vehicleModuleId === finderApplicationModule.vehicleModuleId &&
                        // and also same financing id
                        bankModuleId === finderApplicationModule.bankModuleId &&
                        // should not allow if condition has been added before
                        !conditions.some(condition => finderVehicleConditions.includes(condition))
                    );
                })
                .reduce<SelectFieldProps['options']>(mapOption, []);
        }, [
            conditions,
            finderApplicationModule,
            finderApplicationModuleMapping,
            finderApplicationModules,
            router.company.modules,
        ]);

        return (
            <>
                <InputField
                    {...t('routerDetails:endpoints.fields.pathname', { returnObjects: true })}
                    name="pathname"
                />
                <InputField
                    {...t('routerDetails:endpoints.fields.displayName', { returnObjects: true })}
                    name="displayName"
                />
                <SelectField
                    {...t('routerDetails:endpoints.fields.finderApplicationModuleIds', { returnObjects: true })}
                    mode="multiple"
                    name="finderApplicationModuleIds"
                    options={options}
                    showSearch
                />
            </>
        );
    },
    getFormInitialValues: endpoint => ({
        pathname: endpoint.pathname,
        displayName: endpoint.displayName,
        finderApplicationModuleIds: endpoint.finderApplicationModules.map(module => module.id),
    }),
    submitCreation: (apolloClient, routerId, { finderApplicationModuleIds, ...values }) =>
        apolloClient.mutate<
            CreateFinderApplicationEntrypointMutation,
            CreateFinderApplicationEntrypointMutationVariables
        >({
            mutation: CreateFinderApplicationEntrypointDocument,
            variables: { routerId, finderApplicationModuleIds, settings: values },
        }),
    submitUpdate: (apolloClient, routerId, endpointId, { finderApplicationModuleIds, ...values }) =>
        apolloClient.mutate<
            UpdateFinderApplicationEntrypointMutation,
            UpdateFinderApplicationEntrypointMutationVariables
        >({
            mutation: UpdateFinderApplicationEntrypointDocument,
            variables: { routerId, endpointId, finderApplicationModuleIds, settings: values },
        }),
};

export default implementation;
